import { useEffect, useMemo, useRef, useState } from 'react';
import { Briefcase, Building2, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { useI18n } from '../../context/I18nContext';
import AnimatedEyebrow from '../AnimatedEyebrow';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';

const highlightIcons = [Briefcase, Building2, Sparkles];

function splitTitleParts(text = '') {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return { line1: String(text || '').trim(), line2: '' };
  return {
    line1: words.slice(0, -1).join(' '),
    line2: words[words.length - 1],
  };
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function AboutHeroSection() {
  const { t } = useI18n();
  const [activeMetric, setActiveMetric] = useState(0);
  const glowRef = useRef(null);
  const glowRafRef = useRef(null);
  const highlights = t('aboutPage.hero.highlights');
  const metrics = t('aboutPage.hero.metrics');
  const highlightsTitleParts = useMemo(() => {
    const line1 = String(t('aboutPage.hero.highlightsTitleLine1', '') || '').trim();
    const line2 = String(t('aboutPage.hero.highlightsTitleLine2', '') || '').trim();
    if (line1 || line2) {
      return {
        line1: line1 || String(t('aboutPage.hero.highlightsTitle', '') || '').trim(),
        line2,
      };
    }

    const full = String(t('aboutPage.hero.highlightsTitle', '') || '').trim();
    const words = full.split(/\s+/).filter(Boolean);
    if (words.length <= 2) return { line1: full, line2: '' };
    return {
      line1: words.slice(0, -2).join(' '),
      line2: words.slice(-2).join(' '),
    };
  }, [t]);
  const descriptionParagraphs = useMemo(() => {
    const base = String(t('aboutPage.hero.description', '') || '').trim();
    const secondary = String(t('aboutPage.hero.description2', '') || '').trim();
    return [base, secondary]
      .filter(Boolean)
      .flatMap((block) => block.split(/\n\s*\n/g).map((part) => part.trim()))
      .filter(Boolean);
  }, [t]);

  const titleParts = useMemo(() => {
    const line1 = String(t('aboutPage.hero.titleLine1', '') || '').trim();
    const line2 = String(t('aboutPage.hero.titleLine2', '') || '').trim();
    if (line1 || line2) {
      return {
        line1: line1 || String(t('aboutPage.hero.title', '') || '').trim(),
        line2,
      };
    }

    const full = String(t('aboutPage.hero.title', '') || '').trim();
    const words = full.split(/\s+/).filter(Boolean);
    if (words.length <= 3) return { line1: full, line2: '' };
    const midpoint = Math.ceil(words.length / 2);
    return {
      line1: words.slice(0, midpoint).join(' '),
      line2: words.slice(midpoint).join(' '),
    };
  }, [t]);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveMetric((prev) => (prev + 1) % 2);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  const handleSectionMouseMove = (event) => {
    if (glowRafRef.current) return;
    const clientX = event.clientX;
    const clientY = event.clientY;
    const rect = event.currentTarget.getBoundingClientRect();
    glowRafRef.current = requestAnimationFrame(() => {
      glowRafRef.current = null;
      const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
      if (glowRef.current) {
        glowRef.current.style.backgroundImage = `radial-gradient(circle at ${x}% ${y}%, rgba(255,93,16,0.10) 0%, transparent 42%)`;
      }
    });
  };

  return (
    <section
      className="relative overflow-hidden bg-[var(--dash-blacker)] py-40"
      onMouseMove={handleSectionMouseMove}
    >
      <div className="absolute inset-0 opacity-20 bg-[url('/bg/5.webp')] bg-no-repeat bg-[size:100%_100%]" />
      <div className="pointer-events-none absolute inset-0 z-[1]">
        <div
          ref={glowRef}
          className="absolute left-0 top-0 h-full w-full transition-[background-image] duration-150 ease-out"
          style={{ backgroundImage: 'radial-gradient(circle at 42% 28%, rgba(255,93,16,0.10) 0%, transparent 42%)' }}
        />
      </div>
      <div className="pointer-events-none absolute right-0 top-0 h-[460px] w-[460px] translate-x-1/4 -translate-y-1/3 rounded-full bg-[#ff5d10]/10 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[420px] w-[420px] -translate-x-1/4 translate-y-1/3 rounded-full bg-blue-500/10 blur-[120px]" />

      <div className="container relative z-10 mx-auto px-6 lg:px-10">
        <motion.div
          className="grid gap-12 grid-cols-1 lg:grid-cols-1 xl:grid-cols-[0.90fr_0.95fr] lg:items-stretch"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
        >
          <motion.div className="max-w-5xl" variants={itemVariants}>
            <motion.div variants={itemVariants}>
              <AnimatedEyebrow className="mb-6">{t('aboutPage.hero.eyebrow')}</AnimatedEyebrow>
            </motion.div>
            <motion.h1 variants={itemVariants} className="text-3xl md:text-5xl font-black uppercase leading-[0.95] tracking-[-0.01em] text-white">
              <span className="block font-[500] text-white">{titleParts.line1}</span>
              {titleParts.line2 ? (
                <span className="block bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent [text-shadow:0_8px_30px_rgba(255,93,16,0.25)]">
                  {titleParts.line2}
                </span>
              ) : null}
            </motion.h1>
            <motion.div className="mt-6 w-full xl:max-w-2xl space-y-4" variants={containerVariants}>
              {descriptionParagraphs.map((paragraph, index) => (
                <motion.p
                  key={`${index}-${paragraph.slice(0, 24)}`}
                  variants={itemVariants}
                  className="text-base leading-relaxed text-slate-400 sm:text-lg"
                >
                  {paragraph}
                </motion.p>
              ))}
            </motion.div>
          </motion.div>

          <motion.article className="group relative flex min-h-[330px] flex-col sm:min-h-[260px] lg:h-full lg:min-h-[320px]" variants={itemVariants}>
            <div className="relative h-[330px] w-full overflow-hidden sm:h-[260px] lg:h-full">
              <ImageOnlySkeletonLoader
                src="/about/1.webp"
                mode="native"
                alt="Industrial operations"
                loading="eager"
                fetchPriority="high"
                containerClassName="h-full w-full"
                imgClassName="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {metrics?.experienceValue && metrics?.clientsValue ? (
                <div className="absolute bottom-0 left-0 z-10 hidden lg:block">
                  <div className="relative h-[120px] w-[160px] overflow-hidden">
                    <div
                      className={`absolute inset-0 transition-opacity duration-700 ${
                        activeMetric === 0 ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <MetricCard
                        label={metrics.experienceLabel}
                        value={metrics.experienceValue}
                        tone="bg-[rgba(255,93,16,0.88)]"
                        textClass="text-white"
                        labelClass="text-white"
                      />
                    </div>
                    <div
                      className={`absolute inset-0 transition-opacity duration-700 ${
                        activeMetric === 1 ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <MetricCard
                        label={metrics.clientsLabel}
                        value={metrics.clientsValue}
                        tone="bg-[rgba(228,188,155,0.9)]"
                        textClass="text-white"
                        labelClass="text-white"
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            {metrics?.experienceValue && metrics?.clientsValue ? (
              <div className="relative block w-full lg:hidden">
                <div
                  className={`transition-opacity duration-700 ${
                    activeMetric === 0 ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <MetricCard
                    label={metrics.experienceLabel}
                    value={metrics.experienceValue}
                    tone="bg-[rgba(255,93,16,0.88)]"
                    textClass="text-white"
                    labelClass="text-white"
                  />
                </div>
                <div
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    activeMetric === 1 ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <MetricCard
                    label={metrics.clientsLabel}
                    value={metrics.clientsValue}
                    tone="bg-[rgba(228,188,155,0.9)]"
                    textClass="text-white"
                    labelClass="text-white"
                  />
                </div>
              </div>
            ) : null}
          </motion.article>
        </motion.div>

        {Array.isArray(highlights) && highlights.length > 0 ? (
          <motion.div
            className="relative mt-28 w-full overflow-hidden rounded-3xl border border-white/10 bg-[var(--dash-main)] px-6 py-10 shadow-[0_30px_90px_rgba(0,0,0,0.45)] sm:px-8 lg:px-10"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-120px' }}
          >
            <div className="pointer-events-none absolute inset-0 opacity-100 bg-[url('/bg/2.webp')] bg-no-repeat bg-cover bg-center" />
            <div className="relative z-10">
              <motion.div className="mb-8 flex items-center justify-between gap-4" variants={itemVariants}>
                <motion.h3 className="text-3xl font-light uppercase !leading-[1.2] text-white sm:text-4xl" variants={itemVariants}>
                  {highlightsTitleParts.line1}{' '}
                  {highlightsTitleParts.line2 ? (
                    <span className="font-black text-white">{highlightsTitleParts.line2}</span>
                  ) : null}
                </motion.h3>
                <span className="hidden h-px w-20 bg-[var(--dash-other)]/60 sm:block" />
              </motion.div>

              <motion.ul className="grid w-full gap-4 sm:grid-cols-2 lg:grid-cols-3" variants={containerVariants}>
                {highlights.map((item, index) => (
                  (() => {
                    const Icon = highlightIcons[index % highlightIcons.length];
                    const titleParts = splitTitleParts(item.title);
                    return (
                  <motion.li
                    key={item.title}
                    variants={itemVariants}
                    className="group relative overflow-hidden rounded-2xl border border-white/15 bg-[var(--dash-black)] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--dash-other)]/70 "
                  >
                    <div className="mb-4 flex items-center justify-between">
                      <div className="relative rounded-xl p-3 transition-all duration-500">
                        <div className="absolute inset-0 rounded-xl border-2 border-white/30 transition-all duration-500 group-hover:border-[var(--dash-other)] group-hover:bg-[var(--dash-other)]" />
                        <Icon className="relative z-10 h-6 w-6 text-[var(--dash-other)] transition-colors duration-500 group-hover:text-[var(--dash-black)]" />
                      </div>
                    </div>
                    <p className="text-base font-light uppercase !leading-[1.2] tracking-[0.05em] text-white">
                      {titleParts.line1}{' '}
                      {titleParts.line2 ? <span className="font-bold text-[var(--dash-other)]">{titleParts.line2}</span> : null}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-white/85">{item.detail}</p>
                  </motion.li>
                    );
                  })()
                ))}
              </motion.ul>
            </div>
          </motion.div>
        ) : null}
      </div>
    </section>
  );
}

function MetricCard({ label, value, tone = 'bg-black/55', textClass = 'text-white', labelClass = 'text-white' }) {
  return (
    <div className={`flex h-full w-full flex-col justify-center px-5 py-5 backdrop-blur-sm ${tone}`}>
      <p className={`mt-1 mb-2 text-3xl font-[600] leading-none ${textClass}`}>{value}</p>
      <p className={`text-[11px] font-semibold uppercase tracking-[0.05em] ${labelClass}`}>{label}</p>
    </div>
  );
}
